/**
 * Role Constants
 * Centralized definition of all user roles in the application
 */

export const ROLES = {
  // Admin roles
  ADMIN: "ROLE_ADMIN",
  COMPANY_ADMIN: "ROLE_COMPANY_ADMIN",
  PROJECT_ADMIN: "ROLE_PROJECT_ADMIN",
  KEY_ACCOUNT: "ROLE_KEY_ACCOUNT",
  QUESTIONNAIRE_CREATOR: "ROLE_QUESTIONNAIRE_CREATOR",
  TRANSLATION_MANAGER: "ROLE_TRANSLATION_MANAGER",
  TRANSLATOR: "ROLE_TRANSLATOR", // virtual — checked via delegation records, not DB

  // Participant roles (feedback providers)
  USER: "ROLE_USER",
  SUPERIOR: "ROLE_SUPERIOR",
  COWORKER: "ROLE_COWORKER",
  SUBORDINATE: "ROLE_SUBORDINATE",
  CLIENT: "ROLE_CLIENT",
  LEADERSHIP: "ROLE_LEADERSHIP",
  OTHER: "ROLE_OTHER",
  CONSULTANT: "ROLE_CONSULTANT",
  STAKEHOLDER: "ROLE_STAKEHOLDER",
  SELF: "ROLE_SELF",
  CUSTOM_1: "ROLE_CUSTOM_1",
  CUSTOM_2: "ROLE_CUSTOM_2",
};

/**
 * Role Groups
 * Predefined groups of roles for easier permission checking
 */
export const ROLE_GROUPS = {
  // All admin roles
  ADMIN_ROLES: [
    ROLES.ADMIN,
    ROLES.COMPANY_ADMIN,
    ROLES.PROJECT_ADMIN,
    ROLES.KEY_ACCOUNT,
  ],

  // All participant/feedback provider roles
  PARTICIPANT_ROLES: [
    ROLES.USER,
    ROLES.SUPERIOR,
    ROLES.COWORKER,
    ROLES.SUBORDINATE,
    ROLES.CLIENT,
    ROLES.LEADERSHIP,
    ROLES.OTHER,
    ROLES.CONSULTANT,
    ROLES.STAKEHOLDER,
    ROLES.SELF,
    ROLES.CUSTOM_1,
    ROLES.CUSTOM_2,
  ],

  // High-level admin roles (access to dashboard)
  HIGH_LEVEL_ADMIN: [ROLES.ADMIN, ROLES.COMPANY_ADMIN, ROLES.KEY_ACCOUNT],

  // Project management roles
  PROJECT_MANAGERS: [ROLES.PROJECT_ADMIN, ROLES.COMPANY_ADMIN],

  // Key user types
  KEY_USER_TYPES: [
    ROLES.PROJECT_ADMIN,
    ROLES.QUESTIONNAIRE_CREATOR,
    ROLES.TRANSLATION_MANAGER,
  ],

  // GLOBAL ROLES - roles that are not tied to any specific company
  // These are checked from auth.user.roles
  GLOBAL_ROLES: [
    ROLES.ADMIN, // Only ROLE_ADMIN is truly global
  ],

  // COMPANY-SCOPED ROLES - roles that are specific to a company
  // These are ONLY checked from companyStore.keyUserTypes for the currently selected company
  COMPANY_SCOPED_ROLES: [
    ROLES.COMPANY_ADMIN,
    ROLES.PROJECT_ADMIN,
    ROLES.KEY_ACCOUNT,
    ROLES.QUESTIONNAIRE_CREATOR,
    ROLES.TRANSLATION_MANAGER,
  ],

  // All roles (for profile access etc)
  ALL_ROLES: [
    ROLES.ADMIN,
    ROLES.COMPANY_ADMIN,
    ROLES.PROJECT_ADMIN,
    ROLES.KEY_ACCOUNT,
    ROLES.QUESTIONNAIRE_CREATOR,
    ROLES.TRANSLATION_MANAGER,
    ROLES.USER,
    ROLES.SUPERIOR,
    ROLES.COWORKER,
    ROLES.SUBORDINATE,
    ROLES.CLIENT,
    ROLES.LEADERSHIP,
    ROLES.OTHER,
    ROLES.CONSULTANT,
    ROLES.STAKEHOLDER,
    ROLES.SELF,
    ROLES.CUSTOM_1,
    ROLES.CUSTOM_2,
  ],
};

/**
 * Helper function to check if role is admin role
 */
export const isAdminRole = (role) => {
  return ROLE_GROUPS.ADMIN_ROLES.includes(role);
};

/**
 * Helper function to check if role is participant role
 */
export const isParticipantRole = (role) => {
  return ROLE_GROUPS.PARTICIPANT_ROLES.includes(role);
};
